// Chat DAG — messages → {nodes, edges}.
//
// Walks ConversationContext's messages list and derives a per-turn
// subgraph: user_marker → llm_round_1 → fan-out of tool_calls →
// (consult_peers: further fan-out to peer nodes) → llm_round_2 → …
// → final synthesis round → assistant_marker. Turns are chained
// end-to-start via an edge from the previous assistant_marker to the
// next user_marker.
//
// Inputs intentionally shaped like the rest of the chat surface so
// this function composes cleanly with Wave 6 Task 24's metadata
// shape: `msg.metadata.tool_calls`, each with
// `timing.llm_round`, `identity.tool_call_id`, `identity.tool_name`,
// and `result` (which for consult_peers carries `peers[]`).

import type { Node, Edge } from 'reactflow';
import type { Message } from '../../../../contexts/ConversationContext';
import type { ToolCallEntry } from '../../ToolCallPill';
import type {
  CollapsedPhaseStackData,
  LlmRoundData,
  PeerDagData,
  ToolCallDagData,
  TurnMarkerData,
} from '../types';

// LLM `content` field comes through in three shapes (openrouter
// normalizes but edge cases slip through): plain string (standard),
// null (tool-only reply with no text), or a content-block list
// (Anthropic cache_control path). Coerce to a safe string so
// downstream renderers can rely on `.slice` / `.split` without
// TypeErrors. Mirrors the backend's TokenCounterService._content_to_text
// helper.
function coerceContentToString(value: any): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (Array.isArray(value)) {
    // content-block list: concatenate the `text` of text-typed blocks
    return value
      .map((block) => {
        if (typeof block === 'string') return block;
        if (block && typeof block === 'object') {
          if (typeof block.text === 'string') return block.text;
          if (typeof block.content === 'string') return block.content;
        }
        return '';
      })
      .join('');
  }
  if (typeof value === 'object') {
    // Backend truncation stub from `structured_truncate` —
    // {__truncated: true, bytes: N, sha256_prefix8: '...'}. Pre-2026-04-25
    // turns hit a 512B leaf cap, clipping peer responses + prompts into
    // these stubs. Render a placeholder so ChatMap nodes show *something*
    // instead of empty bubbles.
    if (value.__truncated === true) {
      const bytes: number | undefined = typeof value.bytes === 'number' ? value.bytes : undefined;
      const sizeStr =
        bytes == null
          ? ''
          : bytes < 1024
            ? `${bytes}B`
            : bytes < 1024 * 1024
              ? `${(bytes / 1024).toFixed(1)}KB`
              : `${(bytes / 1024 / 1024).toFixed(1)}MB`;
      return `[truncated by older log policy${sizeStr ? ` · ${sizeStr}` : ''}]`;
    }
    const inner = value.text ?? value.content ?? value.response;
    if (typeof inner === 'string') return inner;
  }
  return '';
}

function toolCallId(call: ToolCallEntry, fallback: string): string {
  return call.identity?.tool_call_id ?? call.tool_call_id ?? fallback;
}

function toolName(call: ToolCallEntry): string {
  return call.identity?.tool_name ?? call.tool_name ?? '';
}

function unwrapResult(call: ToolCallEntry): any {
  const r = call.result;
  if (r && typeof r === 'object' && 'result' in (r as any)) {
    return (r as any).result;
  }
  return r;
}

// Default-expanded density rule (Alan's v0.3 polish ask): fan-outs
// stay fanned out until the user explicitly collapses them. A fan is
// collapsed iff its parent tool_call_id is present in collapsedFanIds.
export function fanCollapsed(
  collapsedFanIds: ReadonlySet<string>,
  toolCallId: string,
): boolean {
  return collapsedFanIds.has(toolCallId);
}

export interface RuntimeState {
  inFlightTurnId: string | null;
  viewportTurnId: string | null;
  // Default-expanded: set contains parent tool_call_ids that the user
  // has opted to fold into a chip stand-in.
  collapsedFanIds: ReadonlySet<string>;
  livePhaseEvents: any[];       // PhaseEvent[] — loosened here to keep processor pure
  wsLastEventMs: number | null;
  // The user message id of a turn that failed (state.status === 'error')
  // so the error-turn sweep can flip orphan runners to 'error' and emit
  // a synthetic assistant marker.
  errorTurnId?: string | null;
  // Controls-panel global override: when true, EVERY consult_peers
  // fan is forced to chip (card-deck) rendering regardless of
  // per-tool state in collapsedFanIds.
  allFansCollapsed?: boolean;
  // Phase-stack collapse state: each turnKey (user message id) in
  // this set has its rounds/tools/peers replaced by a single
  // collapsed_phase_stack card. Feeds the per-turn toggles in the
  // controls panel.
  collapsedPhaseStackIds?: ReadonlySet<string>;
  // Global override: when true, EVERY phase stack is collapsed.
  allPhaseStacksCollapsed?: boolean;
  // v0.4 Task 6: primary persona record for this chat. Attached
  // to assistant turn_marker data so rich variants can render the
  // full PersonaCardBody without each node fetching independently.
  primaryPersona?: any;
}

const EMPTY_RUNTIME: RuntimeState = {
  inFlightTurnId: null,
  viewportTurnId: null,
  collapsedFanIds: new Set(),
  livePhaseEvents: [],
  wsLastEventMs: null,
  errorTurnId: null,
  allFansCollapsed: false,
  collapsedPhaseStackIds: new Set(),
  allPhaseStacksCollapsed: false,
  primaryPersona: null,
};

// True iff this turn's phase stack should render as a single
// collapsed card instead of the full rounds/tools/peers.
function phaseStackCollapsed(
  runtime: RuntimeState,
  turnKey: string,
): boolean {
  if (runtime.allPhaseStacksCollapsed) return true;
  return !!runtime.collapsedPhaseStackIds?.has(turnKey);
}

// Derive a human-readable name for a phase stack from the user
// prompt. Short enough to fit in both the box label + the controls
// list. Whitespace-collapsed so newlines don't blow up single-line
// truncation on rendering. Leading markdown noise (#, *, _, >, `,
// list bullets, etc.) is stripped so a `# Brainstorm session`
// prompt doesn't display its literal hash on the stack label.
function deriveStackName(userContent: string | undefined): string {
  const collapsed = (userContent ?? '').replace(/\s+/g, ' ').trim();
  // Strip leading markdown chrome — heading hashes, blockquote `>`,
  // list bullets `-`/`*`/`+`, surrounding emphasis asterisks/
  // underscores, leading inline-code backticks. Operates only on
  // the leading run; mid-string markdown stays as-is (we render
  // labels as plain text downstream).
  const raw = collapsed.replace(/^[\s>#*_`+\-]+\s*/, '').trim() || collapsed;
  if (!raw) return '(empty prompt)';
  const MAX = 48;
  return raw.length <= MAX ? raw : raw.slice(0, MAX - 1) + '…';
}

function isLatestTurnByIndex(
  turnIndex: number,
  maxIndex: number,
  inFlightTurnId: string | null,
  userMessageId: string,
): boolean {
  if (inFlightTurnId != null) {
    return inFlightTurnId === userMessageId;
  }
  return turnIndex === maxIndex;
}

export function messagesToDag(
  messages: Message[],
  runtime: RuntimeState = EMPTY_RUNTIME,
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  let prevTurnEndId: string | null = null;
  let turnIndex = -1;
  let maxTurnIndex = 0;

  // First pass — count assistant turns for isLatestTurn math.
  for (const m of messages) {
    if (m.role === 'assistant') maxTurnIndex += 1;
  }
  maxTurnIndex = Math.max(0, maxTurnIndex - 1);

  // Edge orientation determines which handles connect. 'spine' runs
  // vertically through the main track (bottom source → top target);
  // 'horizontal' runs sideways for tool branches and peer fans
  // (right source → left target). Matching the rendered geometry
  // keeps smoothstep routing from drawing S-curves through the body
  // of other nodes.
  type EdgeOrient = 'spine' | 'horizontal';
  const addEdge = (
    source: string,
    target: string,
    orient: EdgeOrient = 'spine',
  ) => {
    const sourceHandle = orient === 'spine' ? 'b' : 'r';
    const targetHandle = orient === 'spine' ? 't' : 'l';
    edges.push({
      id: `${source}->${target}`,
      source,
      target,
      sourceHandle,
      targetHandle,
      type: 'default',
    });
  };

  // Tracks the id of the most recently encountered user message so
  // each assistant can pair with its own preceding user, not the
  // last user in the whole array (the naive `reverse().find()` lookup
  // breaks for multi-turn sessions).
  let lastUserId: string | null = null;

  messages.forEach((msg) => {
    if (msg.role !== 'user' && msg.role !== 'assistant') return;

    if (msg.role === 'user') {
      const userId = `user-${msg.id}`;
      nodes.push({
        id: userId,
        type: 'turn_marker',
        data: {
          kind: 'turn_marker',
          role: 'user',
          preview: (msg.content ?? '').slice(0, 80),
          fullContent: msg.content ?? '',
          messageId: msg.id,
          timestamp: msg.timestamp,
          turnIndex: Math.max(0, turnIndex + 1),
          isLatestTurn: false, // fixed up when the assistant lands
          turnKey: msg.id,     // user marker's turnKey is its own id
        } as TurnMarkerData,
        position: { x: 0, y: 0 },
      });
      if (prevTurnEndId) addEdge(prevTurnEndId, userId);
      prevTurnEndId = userId;
      lastUserId = msg.id;
      return;
    }

    // Assistant turn.
    turnIndex += 1;
    const tIdx = turnIndex;
    const userMessageId = lastUserId ?? `unknown-${msg.id}`;
    const latest = isLatestTurnByIndex(
      tIdx,
      maxTurnIndex,
      runtime.inFlightTurnId,
      userMessageId,
    );

    // Retag the most recent user marker's turnIndex to match this
    // assistant's turn so layout grouping (which indexes by turnIndex)
    // sees both halves of the turn together.
    const lastUserNode = [...nodes]
      .reverse()
      .find((n) => n.type === 'turn_marker' && (n.data as any).role === 'user');
    if (lastUserNode) (lastUserNode.data as any).turnIndex = tIdx;

    const toolCalls = (msg.metadata?.tool_calls ?? []) as ToolCallEntry[];
    const roundsMap = new Map<number, ToolCallEntry[]>();
    toolCalls.forEach((call) => {
      const r = Number(call.timing?.llm_round ?? 1);
      if (!roundsMap.has(r)) roundsMap.set(r, []);
      roundsMap.get(r)!.push(call);
    });
    const sortedRounds: Array<[number, ToolCallEntry[]]> =
      roundsMap.size > 0
        ? Array.from(roundsMap.entries()).sort(([a], [b]) => a - b)
        : [[1, []]];
    const lastRoundIdx = sortedRounds.length - 1;

    // v0.3 edge topology (rev: untangle Alan's "visual mess" feedback
    // after the Task 16 flag flip): main spine is a clean vertical at
    // x=0 — user → round-1 → round-2 → … → round-N → asst. Tool calls
    // branch RIGHTWARD off their round as LEAVES with no further
    // chain. Peers branch off consult_peers tools as leaves on the
    // polar arc. No fan-in back to the assistant marker from peers or
    // tools — the spine closes via the last round only.
    const turnStartSpine = prevTurnEndId; // user marker (or prev asst on multi-turn)
    let prevRoundInTurn: string | null = null;

    // Collapsed phase stack: skip rounds/tools/peers emission entirely,
    // emit a single card between user and asst markers.
    if (phaseStackCollapsed(runtime, userMessageId)) {
      const userMsg = messages.find((m) => m.id === userMessageId);
      const ttMeta = (msg.metadata as any)?.turn_telemetry;
      // Count total peers across all consult_peers tool calls.
      let peerCount = 0;
      for (const call of toolCalls) {
        if (toolName(call) !== 'consult_peers') continue;
        const inner = unwrapResult(call);
        const peers: any[] = Array.isArray(inner?.responses)
          ? inner.responses
          : Array.isArray(inner?.peers)
            ? inner.peers
            : Array.isArray(inner?.results)
              ? inner.results
              : [];
        peerCount += peers.length;
      }
      const stackId = `stack-${userMessageId}`;
      nodes.push({
        id: stackId,
        type: 'collapsed_phase_stack',
        data: {
          kind: 'collapsed_phase_stack',
          turnKey: userMessageId,
          turnIndex: tIdx,
          name: deriveStackName(userMsg?.content),
          roundCount: sortedRounds.length,
          toolCount: toolCalls.length,
          peerCount,
          totalCostUsd: ttMeta?.estimated_cost_usd ?? null,
          totalTokens: ttMeta?.total_tokens ?? null,
          isLatestTurn: latest,
          messageId: msg.id,
        } as CollapsedPhaseStackData,
        position: { x: 0, y: 0 },
      });
      if (turnStartSpine) addEdge(turnStartSpine, stackId);
      prevRoundInTurn = stackId;
    } else {

    sortedRounds.forEach(([round, calls], i) => {
      const isSynthesis = i === lastRoundIdx;
      // Stable ID across speculative + canonical: uses userMessageId,
      // NOT the assistant message id — the assistant id doesn't exist
      // until the turn commits, but speculative rounds must exist
      // before that.
      const roundId = `round-${userMessageId}-${round}`;
      // Synthesis-only enrichment — turn totals + answer preview from
      // the asst message metadata. Lets the synthesis node render as
      // a "delivery card" instead of stopping at "writing the answer."
      const _ttMeta = isSynthesis ? (msg.metadata as any)?.turn_telemetry : null;
      const _answerStr = isSynthesis ? (msg.content ?? '') : '';
      // Slightly bigger preview now that the node uses MarkdownPreview
      // (which clips by max-height); ~180 chars feeds 3-4 lines comfortably.
      const _answerPreview = isSynthesis && _answerStr.length > 0
        ? (_answerStr.length > 180 ? `${_answerStr.slice(0, 180).trimEnd()}…` : _answerStr)
        : undefined;
      // Non-synth rounds: surface which tools fanned out, by name +
      // call order. Drives the "→ search · roster · …" at-a-glance
      // line on the round node.
      const _toolNames = isSynthesis
        ? undefined
        : calls.map((c) =>
            (c?.identity?.tool_name ?? c?.tool_name ?? '').replace(/^admin_/, ''),
          ).filter((s) => s.length > 0);
      nodes.push({
        id: roundId,
        type: 'llm_round',
        data: {
          kind: 'llm_round',
          turnId: msg.id,
          round,
          modelUsed: (msg.metadata as any)?.model,
          isSynthesis,
          toolCallCount: calls.length,
          runState: 'completed',
          turnTotalTokens: isSynthesis ? (_ttMeta?.total_tokens ?? null) : undefined,
          turnTotalCostUsd: isSynthesis ? (_ttMeta?.estimated_cost_usd ?? null) : undefined,
          turnDurationMs: isSynthesis ? (_ttMeta?.total_duration_ms ?? null) : undefined,
          turnAnswerPreview: _answerPreview,
          turnTimestamp: isSynthesis ? msg.timestamp : undefined,
          toolNames: _toolNames,
          turnIndex: tIdx,
          isLatestTurn: latest,
          messageId: msg.id,
          turnKey: userMessageId,
        } as LlmRoundData,
        position: { x: 0, y: 0 },
      });
      // Spine edge: previous round (or turn-start) → this round
      const spineParent = prevRoundInTurn ?? turnStartSpine;
      if (spineParent) addEdge(spineParent, roundId);

      calls.forEach((call, ci) => {
        const tcId = `tc-${toolCallId(call, `${userMessageId}-${round}-${ci}`)}`;
        nodes.push({
          id: tcId,
          type: 'tool_call',
          data: {
            kind: 'tool_call',
            call,
            messageId: msg.id,
            round,
            runState: 'completed',
            turnIndex: tIdx,
            isLatestTurn: latest,
            turnKey: userMessageId,
          } as ToolCallDagData,
          position: { x: 0, y: 0 },
        });
        addEdge(roundId, tcId, 'horizontal');

        // consult_peers peer children — canonical expand path, with
        // the FanCollapsedChip substitution gated by fanCollapsed().
        // Peers are LEAVES — no fan-in to the assistant marker.
        if (toolName(call) === 'consult_peers') {
          const inner = unwrapResult(call);
          const rawPeers: any[] = Array.isArray(inner?.responses)
            ? inner.responses
            : Array.isArray(inner?.peers)
              ? inner.peers
              : Array.isArray(inner?.results)
                ? inner.results
                : [];
          const parentToolCallId = toolCallId(
            call,
            `${userMessageId}-${round}-${ci}`,
          );
          const collapsed =
            !!runtime.allFansCollapsed ||
            fanCollapsed(runtime.collapsedFanIds, parentToolCallId);

          if (collapsed && rawPeers.length > 0) {
            const okCount = rawPeers.filter((p) => p?.status === 'ok').length;
            const offlineCount = rawPeers.filter(
              (p) => p?.status === 'offline',
            ).length;
            const errorCount = rawPeers.filter(
              (p) =>
                p?.status === 'error' ||
                p?.status === 'timeout' ||
                p?.status === 'budget_exceeded',
            ).length;
            const totalDurationMs =
              rawPeers.reduce(
                (sum, p) =>
                  sum + (typeof p?.duration_ms === 'number' ? p.duration_ms : 0),
                0,
              ) || null;
            const rInner = inner as any;
            const totalTokens =
              rInner?.total_usage?.total_tokens ?? null;

            const chipId = `${tcId}-chip`;
            nodes.push({
              id: chipId,
              type: 'fan_collapsed_chip',
              data: {
                kind: 'fan_collapsed_chip',
                parentToolCallId,
                peerCount: rawPeers.length,
                okCount,
                offlineCount,
                errorCount,
                totalDurationMs,
                totalTokens,
                turnIndex: tIdx,
                isLatestTurn: latest,
                messageId: msg.id,
                turnKey: userMessageId,
              },
              position: { x: 0, y: 0 },
            });
            addEdge(tcId, chipId, 'horizontal');
          } else {
            // Shared consult_peers prompt + context — same for every
            // peer in the fan — pulled off the call's args. Peer
            // actually received `context + "\n\n" + prompt` as its
            // user_message (see consult_peers_tool.py _invoke_peer).
            // coerceContentToString tolerates the backend truncation
            // stub (legacy 512B leaf cap) so older turns still render
            // a placeholder instead of nothing.
            const _rawPrompt = (call.arguments as any)?.prompt;
            const consultPrompt = (() => {
              const s = coerceContentToString(_rawPrompt);
              return s.length > 0 ? s : undefined;
            })();
            const _rawContext = (call.arguments as any)?.context;
            const consultContext = (() => {
              const s = coerceContentToString(_rawContext);
              return s.length > 0 ? s : undefined;
            })();
            rawPeers.forEach((peer, pi) => {
              const personaId = peer?.peer_persona_id ?? peer?.persona_id;
              const personaName =
                peer?.peer_persona_name ?? peer?.persona_name ?? peer?.name;
              const peerId = `${tcId}-peer-${personaId ?? pi}`;
              const usage = peer?.usage ?? {};
              nodes.push({
                id: peerId,
                type: 'peer',
                data: {
                  kind: 'peer',
                  personaName,
                  personaId,
                  status: peer?.status ?? 'ok',
                  durationMs: peer?.duration_ms,
                  response: coerceContentToString(peer?.response ?? peer?.content),
                  parentToolCallId: tcId.replace(/^tc-/, ''),
                  runState: 'completed',
                  turnIndex: tIdx,
                  isLatestTurn: latest,
                  messageId: msg.id,
                  turnKey: userMessageId,
                  prompt: consultPrompt,
                  context: consultContext,
                  model: peer?.model,
                  error: peer?.error,
                  promptTokens: usage?.prompt_tokens,
                  completionTokens: usage?.completion_tokens,
                  totalTokens: usage?.total_tokens,
                  costUsd: usage?.cost,
                  // Per-peer resolver telemetry from consult_peers —
                  // tells operator how this specific peer got picked.
                  matchedVia: peer?.matched_via,
                  matchedTag: peer?.matched_tag ?? null,
                  matchScore: peer?.match_score,
                  candidateCount: peer?.candidate_count,
                  matchQueries: Array.isArray(peer?.match_queries)
                    ? (peer.match_queries as string[])
                    : peer?.match_query
                      ? [peer.match_query as string]
                      : undefined,
                } as PeerDagData,
                position: { x: 0, y: 0 },
              });
              addEdge(tcId, peerId, 'horizontal');
            });
          }
        }
      });

      prevRoundInTurn = roundId;
    });
    } // end: normal (not-collapsed) phase-stack emission branch

    const asstId = `asst-${msg.id}`;
    const ttMeta = (msg.metadata as any)?.turn_telemetry;
    nodes.push({
      id: asstId,
      type: 'turn_marker',
      data: {
        kind: 'turn_marker',
        role: 'assistant',
        preview: (msg.content ?? '').slice(0, 80),
        fullContent: msg.content ?? '',
        modelUsed: (msg.metadata as any)?.model,
        messageId: msg.id,
        timestamp: msg.timestamp,
        totalCostUsd: ttMeta?.estimated_cost_usd ?? null,
        totalTokens: ttMeta?.total_tokens ?? null,
        toolCallCount: toolCalls.length,
        turnIndex: tIdx,
        isLatestTurn: latest,
        turnKey: userMessageId,
        primaryPersona: runtime.primaryPersona ?? null,
      } as TurnMarkerData,
      position: { x: 0, y: 0 },
    });
    // Spine closes: last round → asst (or turn-start → asst if the
    // turn had no rounds at all — edge case but well-defined).
    const asstSpineParent = prevRoundInTurn ?? turnStartSpine;
    if (asstSpineParent) addEdge(asstSpineParent, asstId);
    prevTurnEndId = asstId;
  });

  // Task 12: if a turn is in flight (user sent but no assistant landed
  // yet), synthesize speculative nodes from runtime.livePhaseEvents so
  // the DAG reflects "thinking…" state during long tool runs.
  emitSpeculativeTurn(nodes, edges, messages, runtime);

  // Task 13: reconcile sweeps.
  orphanRunningSweep(nodes, messages);
  errorTurnSweep(nodes, runtime);

  return { nodes, edges };
}

// Flip any `running` node whose turnKey has a canonical assistant
// message to `completed` — defensive backstop for when the canonical
// loop's id-based reconcile missed a stale speculative node (backend
// dropped a tool_call_id, or provider retry minted a different id).
// When the orphan is a tool_call whose id is NOT in the canonical
// message's metadata.tool_calls, log chat.dag.id_drift — that's the
// signal the architect-triad keystone asked us to emit (spec rev 1.1
// § "Identity drift observability").
function orphanRunningSweep(nodes: Node[], messages: Message[]): void {
  const assistantByUser = new Map<string, Message>();
  let lastUser: string | null = null;
  for (const m of messages) {
    if (m.role === 'user') {
      lastUser = m.id;
    } else if (m.role === 'assistant' && lastUser) {
      assistantByUser.set(lastUser, m);
    }
  }
  if (assistantByUser.size === 0) return;
  for (const n of nodes) {
    const d = n.data as any;
    if (d?.runState === 'running' && d?.turnKey && assistantByUser.has(d.turnKey)) {
      const asst = assistantByUser.get(d.turnKey)!;
      // Drift check: is this a tool_call node whose id is NOT in the
      // canonical tool_calls list? That's the id-drift signal.
      if (n.type === 'tool_call' && n.id.startsWith('tc-')) {
        const specId = n.id.slice(3); // strip 'tc-'
        const canonicalIds = new Set(
          (((asst.metadata as any)?.tool_calls ?? []) as any[])
            .map((c) => c?.identity?.tool_call_id ?? c?.tool_call_id)
            .filter(Boolean),
        );
        if (!canonicalIds.has(specId)) {
          // eslint-disable-next-line no-console
          console.warn('[chat.dag.id_drift]', {
            speculative: n.id,
            turnKey: d.turnKey,
            canonicalIds: Array.from(canonicalIds),
          });
        }
      }
      // eslint-disable-next-line no-console
      console.warn('[chat.dag.orphan_speculative]', {
        id: n.id,
        turnKey: d.turnKey,
      });
      d.runState = 'completed';
    }
  }
}

// When state.status === 'error' for the in-flight turn, flip running
// nodes in that turn to 'error' and emit a synthetic assistant marker
// so the DAG visually closes the turn.
function errorTurnSweep(nodes: Node[], runtime: RuntimeState): void {
  const errId = runtime.errorTurnId;
  if (!errId) return;
  for (const n of nodes) {
    const d = n.data as any;
    if (d?.turnKey === errId && d?.runState === 'running') {
      d.runState = 'error';
    }
  }
  const existing = nodes.find(
    (n) =>
      n.type === 'turn_marker' &&
      (n.data as any)?.role === 'assistant' &&
      (n.data as any)?.turnKey === errId,
  );
  if (!existing) {
    const userNode = nodes.find((n) => n.id === `user-${errId}`);
    const tIdx = (userNode?.data as any)?.turnIndex ?? 9999;
    nodes.push({
      id: `asst-error-${errId}`,
      type: 'turn_marker',
      data: {
        kind: 'turn_marker',
        role: 'assistant',
        preview: '(error — see chat for details)',
        messageId: `asst-error-${errId}`,
        turnIndex: tIdx,
        isLatestTurn: false,
        turnKey: errId,
        primaryPersona: runtime.primaryPersona ?? null,
      } as TurnMarkerData,
      position: { x: 0, y: 0 },
    });
  }
}

// When a turn is in flight (user has sent but the assistant
// message hasn't landed), synthesize nodes for the speculative round +
// any live-in-progress tools/peers based on runtime.livePhaseEvents.
// Node IDs are stable (round-<userMessageId>-<round>, tc-<tool_call_id>,
// <parentTcId>-peer-<peer_persona_id>) so the canonical emission in
// the main loop will match-by-id when the assistant message lands.
function emitSpeculativeTurn(
  nodes: Node[],
  edges: Edge[],
  messages: Message[],
  runtime: RuntimeState,
): void {
  const userMessageId = runtime.inFlightTurnId;
  if (!userMessageId) return;
  const userMsg = messages.find(
    (m) => m.id === userMessageId && m.role === 'user',
  );
  if (!userMsg) return;

  // Skip when a matching assistant has already landed — main loop
  // handled the canonical emission; livePhaseEvents are stale.
  const userIdx = messages.findIndex((m) => m.id === userMessageId);
  const followingAssistant = messages
    .slice(userIdx + 1)
    .find((m) => m.role === 'assistant');
  if (followingAssistant) return;

  // User marker: the main loop already emitted one for every user
  // message in `messages`. If somehow missing (shouldn't happen with
  // current flow), emit a stub.
  let userNode = nodes.find((n) => n.id === `user-${userMessageId}`);
  if (!userNode) {
    userNode = {
      id: `user-${userMessageId}`,
      type: 'turn_marker',
      data: {
        kind: 'turn_marker',
        role: 'user',
        preview: (userMsg.content ?? '').slice(0, 80),
        messageId: userMessageId,
        timestamp: userMsg.timestamp,
        turnIndex: 9999, // sort last; layout sees it above any completed turn
        isLatestTurn: true,
        turnKey: userMessageId,
      } as TurnMarkerData,
      position: { x: 0, y: 0 },
    };
    nodes.push(userNode);
  }
  const tIdx = (userNode.data as any).turnIndex ?? 9999;

  // Speculative addEdge mirrors the canonical path: 'spine' uses
  // bottom/top handles, 'horizontal' uses right/left. Dedup-guarded
  // so repeated phase events don't pile up edge dupes.
  type EdgeOrient = 'spine' | 'horizontal';
  const addEdge = (
    source: string,
    target: string,
    orient: EdgeOrient = 'spine',
  ) => {
    const id = `${source}->${target}`;
    if (edges.some((e) => e.id === id)) return;
    edges.push({
      id,
      source,
      target,
      sourceHandle: orient === 'spine' ? 'b' : 'r',
      targetHandle: orient === 'spine' ? 't' : 'l',
      type: 'default',
    });
  };

  // Ensure a running round exists; emitRound is idempotent. Rounds
  // chain on the main spine (user → round-1 → round-2 → …) so
  // speculative structure matches the canonical topology one-for-one.
  const roundsEmitted = new Set<number>();
  let prevSpeculativeRoundId: string | null = null;
  const emitRound = (round: number) => {
    if (roundsEmitted.has(round)) return;
    roundsEmitted.add(round);
    const id = `round-${userMessageId}-${round}`;
    // If the canonical main loop already emitted this round, adopt it
    // as the spine cursor without re-emitting.
    if (nodes.some((n) => n.id === id)) {
      prevSpeculativeRoundId = id;
      return;
    }
    nodes.push({
      id,
      type: 'llm_round',
      data: {
        kind: 'llm_round',
        turnId: userMessageId,
        round,
        isSynthesis: false,
        toolCallCount: 0,
        runState: 'running',
        turnIndex: tIdx,
        isLatestTurn: true,
        messageId: userMessageId,
        turnKey: userMessageId,
      } as LlmRoundData,
      position: { x: 0, y: 0 },
    });
    const spineParent =
      prevSpeculativeRoundId ?? `user-${userMessageId}`;
    addEdge(spineParent, id);
    prevSpeculativeRoundId = id;
  };
  emitRound(1);

  // Walk phase events and update/append speculative nodes.
  for (const ev of runtime.livePhaseEvents) {
    const kind = ev?.kind;
    const details = (ev?.details ?? {}) as Record<string, any>;

    if (kind === 'chat_tool_invoked') {
      const round = Number(details.llm_round ?? 1);
      emitRound(round);
      // Task 14: some LLM providers (DeepSeek, OpenRouter on retry)
      // can hand back a null tool_call_id in the first chunk of a
      // streamed delta. Synthesize a deterministic fallback so the
      // speculative node still appears; log so we know it happened.
      let tcCallId = details.tool_call_id;
      if (!tcCallId) {
        // eslint-disable-next-line no-console
        console.warn('[chat.dag.null_tool_call_id]', {
          userMessageId,
          round,
          tool_name: details.tool_name,
        });
        tcCallId = `fallback-${userMessageId}-${round}-${nodes.length}`;
      }
      const tcId = `tc-${tcCallId}`;
      if (!nodes.some((n) => n.id === tcId)) {
        nodes.push({
          id: tcId,
          type: 'tool_call',
          data: {
            kind: 'tool_call',
            call: {
              identity: {
                tool_call_id: tcCallId,
                tool_name: details.tool_name,
              },
              timing: { llm_round: round, duration_ms: 0 },
              outcome: { status: 'ok' },
            } as any,
            messageId: userMessageId,
            round,
            runState: 'running',
            turnIndex: tIdx,
            isLatestTurn: true,
            turnKey: userMessageId,
          } as ToolCallDagData,
          position: { x: 0, y: 0 },
        });
        addEdge(`round-${userMessageId}-${round}`, tcId, 'horizontal');
      }
    } else if (
      kind === 'chat_tool_completed' ||
      kind === 'chat_tool_timeout' ||
      kind === 'chat_tool_error' ||
      kind === 'chat_tool_budget_exceeded'
    ) {
      const tcId = `tc-${details.tool_call_id}`;
      const n = nodes.find((nn) => nn.id === tcId);
      if (n) {
        const rawStatus =
          details.status ??
          (kind === 'chat_tool_timeout'
            ? 'timeout'
            : kind === 'chat_tool_error'
              ? 'error'
              : kind === 'chat_tool_budget_exceeded'
                ? 'budget_exceeded'
                : 'ok');
        (n.data as any).runState =
          rawStatus === 'ok' ? 'completed' : rawStatus;
        if (typeof details.duration_ms === 'number') {
          const call = (n.data as any).call;
          if (call?.timing) call.timing.duration_ms = details.duration_ms;
        }
      }
    } else if (kind === 'chat_peer_call_started') {
      const parentId = details.parent_tool_call_id;
      if (!parentId) continue;
      const parentTcId = `tc-${parentId}`;
      const peerPersonaId = details.peer_persona_id;
      const peerId = `${parentTcId}-peer-${peerPersonaId ?? 'unknown'}`;
      if (!nodes.some((n) => n.id === peerId)) {
        nodes.push({
          id: peerId,
          type: 'peer',
          data: {
            kind: 'peer',
            personaName: details.peer_persona_name,
            personaId: peerPersonaId,
            status: 'ok',
            durationMs: 0,
            response: '',
            parentToolCallId: parentId,
            runState: 'running',
            turnIndex: tIdx,
            isLatestTurn: true,
            turnKey: userMessageId,
            messageId: userMessageId,
          } as PeerDagData,
          position: { x: 0, y: 0 },
        });
        addEdge(parentTcId, peerId, 'horizontal');
      }
    } else if (
      kind === 'chat_peer_call_completed' ||
      kind === 'chat_peer_call_failed' ||
      kind === 'chat_peer_call_timeout'
    ) {
      const parentId = details.parent_tool_call_id;
      const peerPersonaId = details.peer_persona_id;
      const peerId = `tc-${parentId}-peer-${peerPersonaId ?? 'unknown'}`;
      const n = nodes.find((nn) => nn.id === peerId);
      if (n) {
        const rawStatus =
          details.status ??
          (kind === 'chat_peer_call_failed'
            ? 'error'
            : kind === 'chat_peer_call_timeout'
              ? 'timeout'
              : 'ok');
        (n.data as any).runState =
          rawStatus === 'ok' ? 'completed' : rawStatus;
        if (typeof details.duration_ms === 'number') {
          (n.data as any).durationMs = details.duration_ms;
        }
      }
    }
  }
}
